<template>
  <main class="result-screen cb-page">
    <header class="result-screen__header">
      <p class="result-screen__eyebrow">Раунд завершён</p>
      <h1>{{ outcomeKnown ? (isWinner ? 'Вы победили' : 'Вы проиграли') : 'Результат раунда' }}</h1>
      <p v-if="result.winnerName" class="result-screen__winner">Победил {{ result.winnerName }}</p>
      <p v-if="game.privateRoom" class="result-screen__notice">Дружеская дуэль · без звёзд и изменений общего рейтинга</p>
      <p v-if="game.postRoundNotice" class="result-screen__notice" role="status">{{ game.postRoundNotice }}</p>
    </header>

    <section class="result-panel result-players" aria-label="Участники раунда">
      <article v-for="entry in participants" :key="entry.self ? 'me' : 'opponent'" class="result-player">
        <PlayerAvatar :src="entry.player && entry.player.avatarUrl" :name="entry.name" :size="52" />
        <div class="result-player__details">
          <h2 class="cb-ellipsis" :title="entry.name">{{ entry.name }}<span v-if="entry.self"> · Вы</span></h2>
          <span v-if="entry.winner" class="result-player__badge">Победитель</span>
          <p v-if="game.privateRoom" class="result-player__stats">Дружеская игра</p>
          <p v-else-if="entry.fresh && entry.player" class="result-player__stats">
            {{ entry.player.countWins }} {{ pluralRu(entry.player.countWins, 'победа', 'победы', 'побед') }}
            · {{ entry.player.countLosses }} {{ pluralRu(entry.player.countLosses, 'поражение', 'поражения', 'поражений') }}
          </p>
          <p v-else class="result-player__stats">{{ refreshing ? 'Обновляем статистику…' : 'Статистика недоступна' }}</p>
        </div>
      </article>
    </section>
    <div v-if="refreshError" class="result-screen__retry" role="status">
      <span>{{ refreshError }}</span>
      <button type="button" :disabled="refreshing" @click="refresh">Повторить</button>
    </div>

    <section v-if="!game.privateRoom" class="result-panel result-rating" aria-labelledby="result-rating-title">
      <h2 id="result-rating-title">Вы в рейтинге</h2>
      <RatingTable v-if="ratingLoading || nearby.length" :rows="nearby" :my-user-id="session.userId" :loading="ratingLoading" label="Вы и соседние игроки" />
      <div v-else-if="ratingError" class="result-rating__state" role="alert">
        <p>Не удалось обновить рейтинг</p>
        <AppButton variant="ghost" size="sm" @click="loadRating">Повторить</AppButton>
      </div>
      <p v-else class="result-rating__state">{{ mine && mine.rank ? 'Соседние позиции пока недоступны' : 'Позиция появится после первой игры' }}</p>
      <button class="result-rating__link" type="button" @click="router.push('/leaderboard')">
        <span>Открыть лидерборд</span><AppIcon name="chevron-right" :size="20" />
      </button>
    </section>

    <footer class="result-screen__actions">
      <AppButton block size="md" :loading="starting" @click="nextGame">{{ game.privateRoom ? 'В комнату' : 'Следующая игра' }}</AppButton>
      <AppButton block variant="ghost" size="md" :disabled="starting" @click="router.replace('/')">Главное меню</AppButton>
    </footer>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import PlayerAvatar from "@/components/ui/PlayerAvatar.vue";
import { getGameResult, getOpponentInfo, leaveRoom } from "@/services/api";
import { acquireCamera, cameraErrorMessage, releaseCamera } from "@/services/media";
import telegram from "@/services/telegram";
import { useGameStore } from "@/stores/game";
import { useSessionStore } from "@/stores/session";
import { useToast } from "@/composables/useToast";
import { pluralRu } from "@/utils/format";
import { useGameOutcome } from "./game/useGameOutcome";
import RatingTable from "./main/RatingTable.vue";
import { useRatingContext } from "./main/useRatingContext";

const router = useRouter();
const game = useGameStore();
const session = useSessionStore();
const toast = useToast();
const { result, isWinner } = useGameOutcome();
const { mine, nearby, loading: ratingLoading, error: ratingError, load: loadRating } = useRatingContext(session, 1);
const starting = ref(false);
const refreshing = ref(false);
const refreshError = ref("");
const meFresh = ref(false);
const opponentFresh = ref(false);
let disposed = false;

const outcomeKnown = computed(() => !!result.value.outcome || (
  session.userId !== null && result.value.winnerUserId !== null && result.value.winnerUserId !== undefined
));
const participants = computed(() => {
  const opponent = {
    self: false,
    player: game.opponent,
    name: game.opponent?.playerName || (outcomeKnown.value ? (isWinner.value ? result.value.loserName : result.value.winnerName) : null) || 'Соперник',
    winner: outcomeKnown.value && !isWinner.value,
    fresh: opponentFresh.value,
  };
  const me = {
    self: true,
    player: session.me,
    name: session.displayName || (outcomeKnown.value ? (isWinner.value ? result.value.winnerName : result.value.loserName) : null) || 'Вы',
    winner: outcomeKnown.value && isWinner.value,
    fresh: meFresh.value,
  };
  return isWinner.value ? [me, opponent] : [opponent, me];
});

async function refresh() {
  if (refreshing.value) return;
  const uuid = game.uuid;
  refreshing.value = true;
  refreshError.value = "";
  const failures = await Promise.allSettled([
    session.loadMe().then(() => { if (!disposed) meFresh.value = true; }),
    getOpponentInfo(uuid).then((info) => {
      if (!disposed && game.uuid === uuid && info) {
        game.setOpponent(info);
        opponentFresh.value = true;
      }
    }),
    getGameResult(uuid).then((response) => {
      if (!disposed && game.uuid === uuid) game.fillResult(response, session.userId);
    }),
  ]);
  if (disposed) return;
  refreshing.value = false;
  if (failures.some((task) => task.status === "rejected")) refreshError.value = "Не все данные удалось обновить";
}

async function nextGame() {
  if (starting.value) return;
  // The existing search flow owns create-game and every server-side admission check.
  session.setMode(game.mode);
  telegram.haptic("medium");
  if (game.privateRoom) {
    starting.value = true;
    try { if (game.roomId) await leaveRoom(game.roomId); await router.push("/room"); }
    catch (error) { toast.error(error.message || "Не удалось закрыть предыдущую комнату"); }
    finally { starting.value = false; }
    return;
  }
  if (!session.agreementAccepted) {
    router.push({ path: "/agreement", query: { next: "search" } });
    return;
  }
  starting.value = true;
  try {
    await acquireCamera();
    if (disposed) releaseCamera();
    else await router.push("/search");
  } catch (error) {
    if (!disposed) toast.error(cameraErrorMessage(error));
  } finally {
    starting.value = false;
  }
}

onMounted(() => {
  refresh();
  if (!game.privateRoom) loadRating();
});
onBeforeUnmount(() => { disposed = true; });
</script>

<style scoped>
.result-screen { gap: 12px; padding-top: calc(var(--cb-safe-top) + 12px); padding-bottom: calc(var(--cb-safe-bottom) + 16px); }
.result-screen__header { display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; }
.result-screen__eyebrow { font-size: 11px; color: var(--cb-text-2); text-transform: uppercase; letter-spacing: 0.16em; }
.result-screen__header h1 { font-size: clamp(24px, 7vw, 30px); line-height: 1.15; }
.result-screen__winner { font-size: 16px; color: var(--cb-text-2); overflow-wrap: anywhere; }
.result-screen__notice { font-size: 13px; color: var(--cb-text-2); }
.result-panel { border: 1px solid var(--cb-border-strong); border-radius: 16px; background: rgba(255, 255, 255, 0.035); }
.result-players { padding: 0 16px; }
.result-player { display: flex; align-items: center; gap: 14px; padding: 12px 0; }
.result-player + .result-player { border-top: 1px solid var(--cb-border-strong); }
.result-player__details { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; min-width: 0; }
.result-player__details h2 { max-width: 100%; font-size: 16px; font-weight: 700; }
.result-player__badge { border-radius: 99px; padding: 2px 10px; background: var(--cb-lime); color: var(--cb-on-lime); font-size: 11px; font-weight: 700; }
.result-player__stats { font-size: 13px; color: var(--cb-text-2); }
.result-rating { padding-top: 10px; overflow: hidden; }
.result-rating :deep(.rating-table__head) { padding-block: 6px; }
.result-rating :deep(.leaderboard-row) { min-height: 48px; padding-block: 6px; }
.result-rating > h2 { padding: 0 16px 6px; font-size: 20px; }
.result-rating__state { padding: 16px; color: var(--cb-text-2); font-size: 14px; }
.result-rating__link { width: calc(100% - 24px); margin: 0 12px; border: 0; border-top: 1px solid var(--cb-border-strong); background: none; display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 48px; padding: 10px 4px; text-align: left; font-size: 15px; cursor: pointer; }
.result-screen__actions { display: flex; flex-direction: column; gap: 4px; margin-top: auto; padding-top: 4px; }
.result-screen__actions .app-button { height: 48px; font-size: 16px; }
.result-screen__actions .app-button--primary { box-shadow: none; }
.result-screen__retry { display: flex; gap: 8px; align-items: center; justify-content: space-between; font-size: 12px; color: var(--cb-text-2); }
.result-screen__retry button { min-height: 44px; border: 0; background: none; color: var(--cb-lime); cursor: pointer; }
</style>
